import React, { useMemo, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, LinearProgress,
  Button, Avatar,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RedeemIcon from '@mui/icons-material/Redeem';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import StatCard from '../../components/common/StatCard';
import DataTable from '../../components/common/DataTable';
import StatusBadge from '../../components/common/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import db from '../../data/demoDatabase';

const columns = [
  { field: 'date', headerName: 'Date' },
  { field: 'type', headerName: 'Type', renderCell: (row) => (
    <Chip label={row.type} size="small" sx={{
      backgroundColor: row.type === 'Redemption' ? '#FFEBEE' : row.type === 'Bonus' ? '#FFF8E1' : '#E3F2FD',
      color: row.type === 'Redemption' ? '#C62828' : row.type === 'Bonus' ? '#F57F17' : '#1565C0',
      fontWeight: 600, fontSize: '0.7rem',
    }} />
  )},
  { field: 'product', headerName: 'Product' },
  { field: 'description', headerName: 'Description' },
  { field: 'tokens', headerName: 'Tokens', align: 'right', renderCell: (row) => (
    <Typography variant="body2" sx={{ fontWeight: 700, color: String(row.tokens).startsWith('+') ? '#2E7D32' : String(row.tokens).startsWith('-') ? '#C62828' : '#546E7A' }}>
      {row.tokens}
    </Typography>
  )},
  { field: 'status', headerName: 'Status', renderCell: (row) => <StatusBadge status={row.status} /> },
];

function computeLevel(totalReferrals) {
  if (totalReferrals >= 50) return { level: 'Platinum', next: null, target: 50, color: '#4527A0' };
  if (totalReferrals >= 10) return { level: 'Gold', next: 'Platinum', target: 50, color: '#F57F17' };
  return { level: 'Silver', next: 'Gold', target: 10, color: '#546E7A' };
}

export default function UserRewards() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [refreshKey] = useState(0);

  const tokenNotifs = useMemo(() => {
    if (!user) return [];
    return db.notifications
      .getByUserId(user.id)
      .filter((n) => ['token', 'redemption'].includes(n.type));
  }, [user, refreshKey]);

  const rewardHistory = useMemo(
    () =>
      tokenNotifs
        .slice()
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .map((n) => {
          const amount = n.amount != null ? Number(n.amount) : Number(n.metadata?.amount) || 0;
          return {
            id: n.id,
            date: n.createdAt ? new Date(n.createdAt).toISOString().split('T')[0] : '',
            type: n.type === 'token' ? 'Token Credit' : 'Redemption',
            product: '-',
            description: n.message || n.title || '',
            tokens: amount ? (n.type === 'redemption' ? `-${amount}` : `+${amount}`) : '-',
            status: 'Credited',
          };
        }),
    [tokenNotifs]
  );

  const userCases = useMemo(
    () => (user ? db.cases.getByUserId(user.id) : []),
    [user, refreshKey]
  );

  const userRedemptions = useMemo(
    () => (user ? db.redemptions.getByUserId(user.id) : []),
    [user, refreshKey]
  );

  const stats = useMemo(() => {
    const tokenNotifsOnly = tokenNotifs.filter((n) => n.type === 'token');
    const totalEarned = tokenNotifsOnly.reduce(
      (sum, n) => sum + (Number(n.amount) || Number(n.metadata?.amount) || 0),
      0
    );
    const now = new Date();
    const thisMonth = tokenNotifsOnly
      .filter((n) => {
        const d = new Date(n.createdAt);
        return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
      })
      .reduce((sum, n) => sum + (Number(n.amount) || Number(n.metadata?.amount) || 0), 0);
    const redeemed = userRedemptions.reduce(
      (sum, r) => sum + (Number(r.tokensSpent) || 0),
      0
    );
    const successfulReferrals = userCases.filter(
      (c) =>
        c.referralOwnerId === user?.id &&
        c.applicantId !== user?.id &&
        ['Approved', 'Disbursed'].includes(c.status)
    ).length;
    return { totalEarned, thisMonth, redeemed, successfulReferrals };
  }, [tokenNotifs, userCases, userRedemptions, user]);

  const levelInfo = useMemo(() => {
    const totalReferrals = Number(user?.totalReferrals) || stats.successfulReferrals || 0;
    const info = computeLevel(totalReferrals);
    const progressPct = info.next
      ? Math.min(100, Math.round((totalReferrals / info.target) * 100))
      : 100;
    return { ...info, totalReferrals, progressPct };
  }, [user, stats.successfulReferrals]);

  const userTokens = user?.tokenBalance || 0;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#1B365D', mb: 3 }}>My Rewards</Typography>

      {/* Level Card */}
      <Card sx={{
        borderRadius: 3, mb: 3, overflow: 'hidden',
        background: 'linear-gradient(135deg, #1B365D 0%, #2C5282 50%, #00B894 100%)',
      }}>
        <CardContent sx={{ p: 4, color: 'white' }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ width: 64, height: 64, backgroundColor: 'rgba(255,255,255,0.2)' }}>
                  <EmojiEventsIcon sx={{ fontSize: 36, color: '#FFD700' }} />
                </Avatar>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>Your Level</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>{levelInfo.level}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {[1, 2, 3, 4, 5].map((s) => {
                      const filled =
                        levelInfo.level === 'Platinum' ? 5 : levelInfo.level === 'Gold' ? 4 : 2;
                      return (
                        <StarIcon key={s} sx={{ fontSize: 16, color: s <= filled ? '#FFD700' : 'rgba(255,255,255,0.3)' }} />
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>Token Balance</Typography>
                <Typography variant="h3" sx={{ fontWeight: 800 }}>{userTokens}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>tokens available</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {levelInfo.next ? `Progress to ${levelInfo.next}` : 'Top tier reached'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <LinearProgress
                    variant="determinate" value={levelInfo.progressPct}
                    sx={{ flex: 1, height: 8, borderRadius: 4, backgroundColor: 'rgba(255,255,255,0.2)',
                      '& .MuiLinearProgress-bar': { backgroundColor: '#FFD700', borderRadius: 4 } }}
                  />
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {levelInfo.totalReferrals}/{levelInfo.target}
                  </Typography>
                </Box>
                {levelInfo.next && (
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    {Math.max(0, levelInfo.target - levelInfo.totalReferrals)} more successful referrals needed
                  </Typography>
                )}
              </Box>
              <Button variant="contained" endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/redemption')}
                sx={{ mt: 2, backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: 2, textTransform: 'none',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' } }}>
                Redeem Tokens
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard title="Total Earned" value={String(stats.totalEarned)} subtitle="all time" icon={StarIcon} color="#FFD700" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title="This Month" value={String(stats.thisMonth)} subtitle="tokens earned" icon={TrendingUpIcon} color="#00B894" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title="Redeemed" value={String(stats.redeemed)} subtitle="tokens used" icon={RedeemIcon} color="#1B365D" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title="Successful Referrals" value={String(stats.successfulReferrals)} subtitle="approved applications" icon={EmojiEventsIcon} color="#7B1FA2" />
        </Grid>
      </Grid>

      {/* Level Benefits */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Referral Level Benefits</Typography>
          <Grid container spacing={2}>
            {[
              { level: 'Silver', color: '#546E7A', bg: '#ECEFF1', benefits: ['Basic referral rewards', 'Standard token rates', 'Email support'] },
              { level: 'Gold', color: '#F57F17', bg: '#FFF8E1', benefits: ['Enhanced referral rewards', '1.5x token multiplier', 'Priority support', 'Exclusive campaigns'] },
              { level: 'Platinum', color: '#4527A0', bg: '#EDE7F6', benefits: ['Premium referral rewards', '2x token multiplier', 'Dedicated account manager', 'VIP campaigns', 'Early access to new products'] },
            ].map((tier) => ({ ...tier, current: tier.level === levelInfo.level })).map((tier) => (
              <Grid item xs={12} md={4} key={tier.level}>
                <Box sx={{
                  p: 2.5, borderRadius: 2, backgroundColor: tier.bg,
                  border: tier.current ? `2px solid ${tier.color}` : '1px solid transparent',
                  position: 'relative',
                }}>
                  {tier.current && (
                    <Chip label="Current" size="small" sx={{ position: 'absolute', top: 8, right: 8, backgroundColor: tier.color, color: 'white', fontWeight: 700, fontSize: '0.65rem' }} />
                  )}
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: tier.color, mb: 1 }}>{tier.level}</Typography>
                  {tier.benefits.map((b, i) => (
                    <Typography key={i} variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                      &#x2022; {b}
                    </Typography>
                  ))}
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Reward History */}
      <DataTable columns={columns} rows={rewardHistory} title="Reward History" exportable />
    </Box>
  );
}
